import type { JobTask, JobTaskStatus } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { StatusBadge } from '@/components/StatusBadge';

interface TasksTableProps {
  tasks: JobTask[];
  onUpdateStatus?: (taskId: string) => void;
  onCancel?: (taskId: string) => void;
  statusFilter?: JobTaskStatus | '';
}

export function TasksTable({
  tasks,
  onUpdateStatus,
  onCancel,
  statusFilter,
}: TasksTableProps) {
  // Apply status filter (empty string means no filter)
  const filteredTasks = statusFilter
    ? tasks.filter((task) => task.status === statusFilter)
    : tasks;

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        No tasks yet
      </div>
    );
  }

  if (filteredTasks.length === 0 && statusFilter) {
    return (
      <div className="rounded-lg border border-border border-dashed bg-background-subtle p-8 text-center">
        <p className="text-sm text-foreground-muted">
          No tasks found with this status
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-border">
        <table className="w-full">
        <thead className="bg-background-subtle border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Task #
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Progress
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-background divide-y divide-border">
          {filteredTasks.map((task) => {
            return (
              <tr key={task.id} className="hover:bg-background-subtle transition-colors">
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">
                    Task {task.taskNumber}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={task.status} size="sm" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-background-subtle rounded-full overflow-hidden min-w-[100px]">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                    <span className="text-sm text-foreground-muted min-w-[40px] text-right">
                      {Math.round(task.progress)}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {onUpdateStatus && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onUpdateStatus(task.id)}
                      >
                        Update
                      </Button>
                    )}
                    {onCancel && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onCancel(task.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
